import { useState, useEffect, useCallback } from 'react';
import { DollarSign, Users, TrendingUp, Calendar, Download, FileText, Check, Clock, ChevronDown, ChevronUp, Search, RefreshCw, Filter, UserPlus, Trash2, Edit2, Save, X, Settings, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const fmt = (val) => `$${Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const GRADIENT_POOL = [
  'from-blue-500 to-blue-700',
  'from-emerald-500 to-emerald-700',
  'from-amber-500 to-amber-700',
  'from-purple-500 to-purple-700',
  'from-rose-500 to-rose-700',
  'from-cyan-500 to-cyan-700',
  'from-indigo-500 to-indigo-700',
  'from-orange-500 to-orange-700',
];
const getRepColor = (index) => GRADIENT_POOL[index % GRADIENT_POOL.length];

const CommissionTrackerTab = () => {
  const [view, setView] = useState('overview');
  const [repData, setRepData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filterRep, setFilterRep] = useState('');
  const [filterPaid, setFilterPaid] = useState('');
  const [expandedRep, setExpandedRep] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  const [repsList, setRepsList] = useState([]);
  const [newRepName, setNewRepName] = useState('');
  const [newRepTerritory, setNewRepTerritory] = useState('');
  const [editingRep, setEditingRep] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTerritory, setEditTerritory] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editBoroughs, setEditBoroughs] = useState([]);           // ["K", "X", ...]
  const [editQueensDistricts, setEditQueensDistricts] = useState([]); // [24, 30]
  const [repSaving, setRepSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // v6.38.40 — inline PO editor state (replaces window.prompt which is
  // fragile in embedded browsers). Only ONE PO cell edits at a time.
  const [editingPoKey, setEditingPoKey] = useState(null);
  const [editingPoValue, setEditingPoValue] = useState('');
  const [savingPo, setSavingPo] = useState(false);
  const [poFlash, setPoFlash] = useState(null); // last saved order_number for green flash
  const currentYear = new Date().getFullYear();

  const fetchReps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commissions/reps`, authHeader());
      setRepsList(res.data.reps || []);
    } catch {}
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [repRes, monthRes, yearRes, orderRes] = await Promise.all([
        axios.get(`${API}/commissions/by-rep`, authHeader()),
        axios.get(`${API}/commissions/by-month?year=${currentYear}`, authHeader()),
        axios.get(`${API}/commissions/yearly-summary?year=${currentYear}`, authHeader()),
        axios.get(`${API}/commissions/orders`, authHeader()),
      ]);
      setRepData(repRes.data.reps || []);
      setMonthlyData(monthRes.data.months || []);
      setYearlyData(yearRes.data || {});
      setOrders(orderRes.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch commission data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  useEffect(() => { fetchAll(); fetchReps(); }, [fetchAll, fetchReps]);

  const downloadReceipt = async (repName, year, month) => {
    const key = `${repName}-${year}-${month}`;
    setDownloadingReceipt(key);
    try {
      const res = await axios.get(
        `${API}/commissions/payout-receipt/${encodeURIComponent(repName)}/${year}/${month}`,
        { ...authHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_Commission_Payout_${repName.replace(/ /g, '_')}_${year}_${String(month).padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        alert(`No qualifying commissions found for ${repName} in ${MONTH_NAMES[month]} ${year}`);
      } else {
        console.error('Failed to download receipt:', err);
      }
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const [sendingStatement, setSendingStatement] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  // v6.38.41 — Statements Sent ledger + Year-End statement state
  const [statementLog, setStatementLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [downloadingAnnual, setDownloadingAnnual] = useState(null);
  const [sendingAnnual, setSendingAnnual] = useState(null);

  const fetchStatementLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/commissions/statement-log?limit=200`, authHeader());
      setStatementLog(r.data.entries || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchStatementLog(); }, [fetchStatementLog]);

  const downloadAnnual = async (repName, year) => {
    const key = `${repName}-${year}`;
    setDownloadingAnnual(key);
    try {
      const res = await axios.get(
        `${API}/commissions/annual-statement/${encodeURIComponent(repName)}/${year}`,
        { ...authHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_Annual_Statement_${year}_${repName.replace(/ /g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.status === 404
        ? `No commissions found for ${repName} in ${year}.`
        : 'Failed to download annual statement.');
    } finally { setDownloadingAnnual(null); }
  };

  const sendAnnual = async (repName, year, email) => {
    if (!window.confirm(`Email the ${year} year-end 1099 recap to ${repName}${email ? ' <' + email + '>' : ''}?`)) return;
    const key = `${repName}-${year}`;
    setSendingAnnual(key);
    try {
      const r = await axios.post(
        `${API}/commissions/send-annual/${encodeURIComponent(repName)}/${year}`,
        {},
        authHeader()
      );
      alert(`Sent year-end recap to ${r.data.to_email}. Total paid: $${(r.data.total_paid || 0).toFixed(2)}.`);
      await fetchStatementLog();
    } catch (err) {
      alert('Send failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSendingAnnual(null); }
  };

  // v6.38.35 — one-shot "mark every unpaid order for this rep as paid"
  // action. Powers the "Mark All Paid Today" button on each rep card so
  // the user doesn't have to open each order dialog one-by-one.
  const markAllPaid = async (repName, unpaidCount, unpaidTotal) => {
    const today = new Date().toISOString().slice(0, 10);
    const chosen = window.prompt(
      `Mark all ${unpaidCount} unpaid order(s) for ${repName} as PAID.\n\n` +
      `Total commission: $${(unpaidTotal || 0).toFixed(2)}\n\n` +
      `Enter the payout date (YYYY-MM-DD).  Leave blank to use today (${today}).`,
      today
    );
    if (chosen === null) return; // cancelled
    const paidDate = (chosen || today).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paidDate)) {
      alert("Payout date must be YYYY-MM-DD.");
      return;
    }
    setMarkingPaid(repName);
    try {
      const r = await axios.post(
        `${API}/orders/-/mark-paid-bulk`,
        { sales_rep: repName, commission_paid_date: paidDate },
        authHeader()
      );
      alert(
        `Marked ${r.data.marked_count} order(s) as PAID on ${r.data.paid_date}.\n\n` +
        `You can now download or send the ${MONTH_NAMES[Number(paidDate.slice(5, 7))]} ${paidDate.slice(0, 4)} statement below.`
      );
      await fetchAll();
    } catch (err) {
      alert('Mark-paid failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMarkingPaid(null);
    }
  };

  // v6.38.40 — inline PO editor: no window.prompt, no browser blocking.
  // Renders an <input> in the PO # cell when clicked. Save on Enter,
  // cancel on Escape or blur-with-no-change. Green flash on success,
  // red alert on failure.
  const poKey = (o) => o?.order_id || o?.order_number || o?.quote_number;
  const canEditPo = (o) =>
    !!(o?.order_id || (o?.order_number && /^MOS-ORD-/i.test(o.order_number)));

  const startEditPo = (order) => {
    if (!canEditPo(order)) {
      alert('This row is a legacy quote — edit its PO under Fulfillment Tracker.');
      return;
    }
    setEditingPoKey(poKey(order));
    setEditingPoValue(order.po_number || '');
  };

  const cancelEditPo = () => {
    setEditingPoKey(null);
    setEditingPoValue('');
  };

  const savePo = async (order) => {
    const clean = (editingPoValue || '').trim();
    if (clean === (order.po_number || '')) {
      cancelEditPo();
      return;
    }
    setSavingPo(true);
    try {
      // Resolve order_id (fallback to lookup if the commissions payload
      // was cached before the order_id surfacing patch went out).
      let orderId = order.order_id;
      if (!orderId) {
        const r = await axios.get(`${API}/orders`, {
          ...authHeader(),
          params: { order_number: order.order_number },
        });
        orderId = ((r.data.orders || [])[0] || {}).order_id;
      }
      if (!orderId) {
        alert(`Couldn't resolve order_id for ${order.order_number}. Try hard-refreshing.`);
        return;
      }
      const resp = await axios.patch(
        `${API}/orders/${orderId}/commission`,
        { po_number: clean },
        authHeader()
      );
      // Verify the response actually contains the new po_number — if the
      // deployed backend is stale and stripped the field, we'd otherwise
      // show a false-success state.
      const savedPo = resp?.data?.po_number;
      if (savedPo !== undefined && savedPo !== clean) {
        alert(
          `The server didn't accept the po_number field yet.\n\n` +
          `Got back: ${JSON.stringify(savedPo)}\n\n` +
          `The backend redeploy hasn't reached production. Try again in a minute.`
        );
        return;
      }
      setPoFlash(order.order_number);
      setTimeout(() => setPoFlash(null), 1500);
      cancelEditPo();
      await fetchAll();
    } catch (err) {
      alert('PO save failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingPo(false);
    }
  };

  const sendStatement = async (repName, year, month, repEmail) => {
    const key = `${repName}-${year}-${month}`;
    const period = `${MONTH_NAMES[month]} ${year}`;
    let overrideEmail = repEmail;
    if (!overrideEmail) {
      overrideEmail = window.prompt(
        `No email on file for ${repName}.\n\nEnter their email once (or add it permanently under Manage Reps → Edit):`,
        ''
      );
      if (!overrideEmail) return;
    } else {
      if (!window.confirm(
        `Email ${period} payout statement to:\n\n${repName} <${overrideEmail}>\n\nProceed?`
      )) return;
    }
    setSendingStatement(key);
    try {
      const res = await axios.post(
        `${API}/commissions/send-statement/${encodeURIComponent(repName)}/${year}/${month}`,
        { override_email: overrideEmail || undefined },
        authHeader()
      );
      alert(
        `Statement sent to ${res.data.to_email}\n\n` +
        `Period:  ${res.data.period}\n` +
        `Orders:  ${res.data.orders_count}\n` +
        `Total:   $${res.data.total.toFixed(2)}`
      );
      await fetchStatementLog();
    } catch (err) {
      alert('Send failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingStatement(null);
    }
  };

  const addRep = async () => {
    if (!newRepName.trim() || !newRepTerritory.trim()) return;
    setRepSaving(true);
    try {
      await axios.post(`${API}/commissions/reps`, { name: newRepName.trim(), territory: newRepTerritory.trim() }, authHeader());
      setNewRepName('');
      setNewRepTerritory('');
      await Promise.all([fetchReps(), fetchAll()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add rep');
    } finally {
      setRepSaving(false);
    }
  };

  const saveEditRep = async (repId) => {
    if (!editName.trim() || !editTerritory.trim()) return;
    setRepSaving(true);
    try {
      await axios.put(`${API}/commissions/reps/${repId}`, {
        name: editName.trim(),
        territory: editTerritory.trim(),
        email: editEmail.trim() || null,
        boroughs: editBoroughs,
        queens_districts_only: editBoroughs.includes('Q') ? editQueensDistricts : [],
      }, authHeader());
      setEditingRep(null);
      await Promise.all([fetchReps(), fetchAll()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update rep');
    } finally {
      setRepSaving(false);
    }
  };

  const deleteRep = async (repId) => {
    setRepSaving(true);
    try {
      await axios.delete(`${API}/commissions/reps/${repId}`, authHeader());
      setConfirmDelete(null);
      await Promise.all([fetchReps(), fetchAll()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete rep');
    } finally {
      setRepSaving(false);
    }
  };

  const filteredOrders = orders.filter(o => {
    if (filterRep && o.sales_rep !== filterRep) return false;
    if (filterPaid === 'paid' && !o.commission_paid) return false;
    if (filterPaid === 'unpaid' && o.commission_paid) return false;
    return true;
  });

  const maxRevenue = Math.max(...repData.map(r => r.total_revenue), 1);

  return (
    <div className="space-y-5" data-testid="commission-tracker-tab">
      {/* View Toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'overview', label: 'Overview', icon: TrendingUp },
          { key: 'by-rep', label: 'By Sales Rep', icon: Users },
          { key: 'orders', label: 'All Orders', icon: FileText },
          { key: 'monthly', label: 'Monthly', icon: Calendar },
          { key: 'manage', label: 'Manage Reps', icon: Settings },
        ].map(v => (
          <Button
            key={v.key}
            variant={view === v.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView(v.key)}
            className={view === v.key ? 'bg-[#002FA7]' : ''}
            data-testid={`view-${v.key}-btn`}
          >
            <v.icon size={14} className="mr-1" /> {v.label}
          </Button>
        ))}
        <Button
          variant={showLog ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowLog(v => !v)}
          className={showLog ? 'bg-emerald-700 hover:bg-emerald-800' : ''}
          data-testid="toggle-statement-log"
          title="Show/hide the audit trail of every commission statement email sent"
        >
          <Mail size={14} className="mr-1" /> Statements Sent ({statementLog.length})
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!window.confirm(
                'Sync Orders with Commissions?\n\n' +
                'This will scan every order in Orders & Fulfillment. Any order missing a sales rep will get one auto-assigned by borough:\n\n' +
                '  • Brooklyn → John Perez\n' +
                '  • Bronx + Queens D24 + Queens D30 → Cesar Roca\n' +
                '  • Manhattan → Himanshu Jain\n' +
                '  • Queens (D25-D29) + Long Island + Staten Island → David Katz\n\n' +
                'Orders that ALREADY have a rep assigned will be left alone (safe to run multiple times).\n\n' +
                'Default commission rate: 15%. You can override any order manually afterward.'
              )) return;
              setSyncing(true);
              try {
                const r = await axios.post(`${API}/orders/-/backfill-commissions`, {}, authHeader());
                const d = r.data || {};
                const msg = `Sync complete\n\n` +
                  `Updated: ${d.updated_count} orders\n` +
                  `Skipped (no borough detected): ${d.skipped_count}\n\n` +
                  (d.updated || []).slice(0, 6).map(u =>
                    `• ${u.order_number} · ${u.school} → ${u.sales_rep} ($${u.commission_amount})`
                  ).join('\n') +
                  ((d.updated || []).length > 6 ? `\n\n…and ${d.updated.length - 6} more` : '');
                window.alert(msg);
                await fetchAll();
              } catch (e) {
                window.alert('Sync failed: ' + (e.response?.data?.detail || e.message));
              } finally {
                setSyncing(false);
              }
            }}
            disabled={loading || syncing}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="sync-orders-commissions-btn"
            title="Auto-assign a sales rep to every order missing one, by borough"
          >
            <RefreshCw size={14} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Orders with Commissions'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-commissions-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
      </div>

      {/* ═══ STATEMENTS SENT LEDGER ═══ */}
      {showLog && (
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="statement-log-panel">
          <div className="px-4 py-3 border-b bg-emerald-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-emerald-700" />
              <h3 className="font-bold text-slate-900 text-sm">Statements Sent — Audit Trail</h3>
              <span className="text-xs text-slate-500">Every commission statement email that went out</span>
            </div>
            <Button size="sm" variant="ghost" onClick={fetchStatementLog} className="h-7 text-xs">
              <RefreshCw size={12} className="mr-1" /> Refresh
            </Button>
          </div>
          {statementLog.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No statements have been emailed yet. Click ✉ Send next to any receipt to start.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr className="text-gray-600">
                    <th className="text-left px-3 py-2 font-semibold">Sent At (UTC)</th>
                    <th className="text-left px-3 py-2 font-semibold">Rep</th>
                    <th className="text-left px-3 py-2 font-semibold">Period</th>
                    <th className="text-left px-3 py-2 font-semibold">Emailed To</th>
                    <th className="text-right px-3 py-2 font-semibold">Amount</th>
                    <th className="text-center px-3 py-2 font-semibold">Orders</th>
                    <th className="text-left px-3 py-2 font-semibold">Sent By</th>
                    <th className="text-center px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statementLog.map((e, i) => {
                    const period = e.month === 0 || e.kind === 'annual'
                      ? `${e.year} · Year-End`
                      : `${MONTH_NAMES[e.month] || '?'} ${e.year}`;
                    const isAnnual = e.month === 0 || e.kind === 'annual';
                    const key = `${e.rep_name}-${e.year}-${e.month}`;
                    return (
                      <tr key={i} className="border-b hover:bg-emerald-50/30">
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-500 whitespace-nowrap">{fmtDate(e.sent_at)}</td>
                        <td className="px-3 py-2 font-semibold text-slate-800">{e.rep_name}</td>
                        <td className="px-3 py-2">
                          {isAnnual ? <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-[10px] font-bold">{period}</span>
                                    : <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-[10px] font-bold">{period}</span>}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{e.to_email}</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmt(e.total_amount || 0)}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{e.orders_count}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-500">{e.sent_by || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={(isAnnual ? sendingAnnual : sendingStatement) === key}
                            onClick={() => isAnnual
                              ? sendAnnual(e.rep_name, e.year, e.to_email)
                              : sendStatement(e.rep_name, e.year, e.month, e.to_email)}
                            className="h-6 text-[10px] px-2"
                            data-testid={`resend-${i}`}
                            title={`Resend the ${period} statement to ${e.to_email}`}
                          >
                            {(isAnnual ? sendingAnnual : sendingStatement) === key ? '…' : <><Mail size={10} className="mr-1" /> Resend</>}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ OVERVIEW ═══ */}
      {view === 'overview' && (
        <>
          {/* Yearly Summary Cards — clickable to drill into All Orders view */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="yearly-summary">
            <SummaryCard
              icon={FileText}
              value={yearlyData.total_orders || 0}
              label="Closed Orders"
              color="text-[#002FA7]"
              onClick={() => { setFilterPaid(''); setView('orders'); }}
              testid="kpi-closed-orders"
            />
            <SummaryCard
              icon={DollarSign}
              value={fmt(yearlyData.total_revenue)}
              label={`${currentYear} Revenue`}
              color="text-[#002FA7]"
              isText
              onClick={() => { setFilterPaid(''); setView('orders'); }}
              testid="kpi-revenue"
            />
            <SummaryCard
              icon={TrendingUp}
              value={fmt(yearlyData.total_commission)}
              label="Commission Earned"
              color="text-emerald-600"
              isText
              onClick={() => { setFilterPaid(''); setView('orders'); }}
              testid="kpi-earned"
            />
            <SummaryCard
              icon={Check}
              value={fmt(yearlyData.total_paid)}
              label="Commission Paid"
              color="text-green-600"
              isText
              onClick={() => { setFilterPaid('paid'); setView('orders'); }}
              testid="kpi-paid"
              hint="See paid orders"
            />
            <SummaryCard
              icon={Clock}
              value={fmt(yearlyData.total_unpaid)}
              label="Outstanding"
              color="text-amber-600"
              isText
              onClick={() => { setFilterPaid('unpaid'); setView('orders'); }}
              testid="kpi-outstanding"
              hint="See unpaid orders"
            />
          </div>

          {/* Rep Cards */}
          <h3 className="text-sm font-bold text-gray-800 mt-4">Sales Team Performance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {repData.map((rep, repIndex) => {
              const barWidth = maxRevenue > 0 ? (rep.total_revenue / maxRevenue) * 100 : 0;
              const gradient = getRepColor(repIndex);
              return (
                <button
                  type="button"
                  key={rep.name}
                  onClick={() => { setFilterRep(rep.name); setView('orders'); }}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden text-left w-full transition-all hover:shadow-md hover:-translate-y-[1px] hover:border-[#002FA7]/40 cursor-pointer"
                  data-testid={`rep-card-${rep.name.replace(/ /g, '-')}`}
                  title={`Click to see ${rep.name}'s ${rep.total_orders} order${rep.total_orders !== 1 ? 's' : ''}`}
                >
                  <div className={`bg-gradient-to-r ${gradient} px-4 py-2.5`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-sm">{rep.name}</p>
                        <p className="text-white/70 text-[10px]">{rep.territory}</p>
                      </div>
                      <span className="text-white/90 text-xs font-mono bg-white/20 px-2 py-0.5 rounded">
                        {rep.total_orders} order{rep.total_orders !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Revenue</span>
                      <span className="font-bold text-gray-800">{fmt(rep.total_revenue)}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className={`bg-gradient-to-r ${gradient} h-1.5 rounded-full transition-all`} style={{ width: `${barWidth}%` }} />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[10px] pt-1">
                      <div className="bg-gray-50 rounded px-2 py-1.5">
                        <p className="text-gray-400">Earned</p>
                        <p className="font-bold text-gray-700">{fmt(rep.total_commission)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded px-2 py-1.5">
                        <p className="text-emerald-400">Paid</p>
                        <p className="font-bold text-emerald-700">{fmt(rep.total_paid)}</p>
                      </div>
                      <div className="bg-amber-50 rounded px-2 py-1.5">
                        <p className="text-amber-400">Owed</p>
                        <p className="font-bold text-amber-700">{fmt(rep.total_unpaid)}</p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ═══ BY REP ═══ */}
      {view === 'by-rep' && (
        <div className="space-y-3">
          {repData.map((rep, repIndex) => {
            const isExpanded = expandedRep === rep.name;
            const gradient = getRepColor(repIndex);
            // v6.38.37 — Group receipt buttons by commission_paid_date (fallback
            // to close_date for orders that aren't paid yet). Prior versions
            // grouped only by close_date, which caused clicks on April/May
            // buttons to hit the payout endpoint (which keys on paid_date) and
            // return 404, even though the payouts were actually made later.
            const ordersByMonth = {};
            (rep.orders || []).forEach(o => {
              const d = (o.commission_paid && o.commission_paid_date) || o.closed_date || '';
              if (!d) return;
              try {
                const dt = new Date(d);
                if (isNaN(dt.getTime())) return;
                const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
                if (!ordersByMonth[key]) ordersByMonth[key] = [];
                ordersByMonth[key].push(o);
              } catch {}
            });

            return (
              <div key={rep.name} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`rep-detail-${rep.name.replace(/ /g, '-')}`}>
                <button
                  onClick={() => setExpandedRep(isExpanded ? null : rep.name)}
                  className={`w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r ${gradient} text-white hover:opacity-95 transition-opacity`}
                  data-testid={`rep-toggle-${rep.name.replace(/ /g, '-')}`}
                >
                  <div className="text-left">
                    <p className="font-bold text-sm">{rep.name}</p>
                    <p className="text-white/70 text-[10px]">{rep.territory}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono bg-white/20 px-2 py-0.5 rounded">{rep.total_orders} orders | {fmt(rep.total_commission)} earned</span>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="p-4 space-y-3">
                    {/* Stats row */}
                    <div className="grid grid-cols-4 gap-3 text-xs">
                      <div className="bg-gray-50 rounded p-2.5 text-center">
                        <p className="text-gray-400 text-[10px]">Revenue</p>
                        <p className="font-bold text-gray-800">{fmt(rep.total_revenue)}</p>
                      </div>
                      <div className="bg-blue-50 rounded p-2.5 text-center">
                        <p className="text-blue-400 text-[10px]">Commission</p>
                        <p className="font-bold text-blue-800">{fmt(rep.total_commission)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded p-2.5 text-center">
                        <p className="text-emerald-400 text-[10px]">Paid Out</p>
                        <p className="font-bold text-emerald-800">{fmt(rep.total_paid)}</p>
                      </div>
                      <div className="bg-amber-50 rounded p-2.5 text-center">
                        <p className="text-amber-400 text-[10px]">Outstanding</p>
                        <p className="font-bold text-amber-800">{fmt(rep.total_unpaid)}</p>
                      </div>
                    </div>

                    {/* Orders table */}
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-gray-50 border-b text-gray-500">
                          <th className="text-left px-2 py-1.5">NYC PO #</th>
                          <th className="text-left px-2 py-1.5">Ref #</th>
                          <th className="text-left px-2 py-1.5">School</th>
                          <th className="text-right px-2 py-1.5">Order Amt</th>
                          <th className="text-center px-2 py-1.5">Comm %</th>
                          <th className="text-right px-2 py-1.5">Commission</th>
                          <th className="text-center px-2 py-1.5">NYC Paid</th>
                          <th className="text-center px-2 py-1.5">Comm Paid</th>
                          <th className="text-left px-2 py-1.5">Close Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(rep.orders || []).length === 0 ? (
                          <tr><td colSpan={9} className="text-center py-6 text-gray-400">No orders assigned to this rep yet</td></tr>
                        ) : (rep.orders || []).map((o, i) => (
                          <tr key={i} className="border-b hover:bg-blue-50/30">
                            <td
                              className={`px-2 py-1.5 font-mono font-bold text-[#002FA7] ${canEditPo(o) ? 'cursor-pointer hover:bg-blue-100' : ''} ${poFlash === o.order_number ? 'bg-green-100' : ''}`}
                              onClick={() => editingPoKey === poKey(o) ? null : startEditPo(o)}
                              title={canEditPo(o) ? 'Click to edit NYC PO #' : 'Legacy quote — edit under Fulfillment Tracker'}
                              data-testid={`po-cell-${o.order_number || o.quote_number}`}
                            >
                              {editingPoKey === poKey(o) ? (
                                <input
                                  autoFocus
                                  type="text"
                                  className="border border-blue-500 rounded px-1 py-0.5 text-xs w-32 font-mono"
                                  value={editingPoValue}
                                  onChange={(e) => setEditingPoValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); savePo(o); }
                                    else if (e.key === 'Escape') { e.preventDefault(); cancelEditPo(); }
                                  }}
                                  onBlur={() => setTimeout(() => { if (editingPoKey === poKey(o) && !savingPo) savePo(o); }, 150)}
                                  disabled={savingPo}
                                  placeholder="WO260…"
                                  data-testid={`po-input-${o.order_number}`}
                                />
                              ) : (
                                o.po_number || <span className="text-gray-300 italic">click to add</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px] text-gray-500">{o.quote_number || o.order_number || <span className="text-gray-300">—</span>}</td>
                            <td className="px-2 py-1.5 text-gray-700">{o.school_name}</td>
                            <td className="px-2 py-1.5 text-right">{fmt(o.subtotal)}</td>
                            <td className="px-2 py-1.5 text-center">{o.commission_pct}%</td>
                            <td className="px-2 py-1.5 text-right font-bold">{fmt(o.commission_amount)}</td>
                            <td className="px-2 py-1.5 text-center">
                              {o.payment_received ? <Check size={12} className="text-emerald-500 mx-auto" /> : <Clock size={12} className="text-gray-300 mx-auto" />}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {o.commission_paid
                                ? <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">PAID</span>
                                : <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">OWED</span>
                              }
                            </td>
                            <td className="px-2 py-1.5 text-gray-500">{fmtDate(o.closed_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Bulk mark-paid action + Monthly Receipt Downloads */}
                    {(rep.orders || []).length > 0 && (() => {
                      const unpaidOrders = (rep.orders || []).filter(o => !o.commission_paid);
                      const unpaidTotal = unpaidOrders.reduce((s, o) => s + (o.commission_amount || 0), 0);
                      const hasUnpaid = unpaidOrders.length > 0;
                      return (
                        <div className="border-t pt-3 space-y-3">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-[11px] text-gray-600">
                              <span className="font-bold">{unpaidOrders.length}</span> unpaid order{unpaidOrders.length !== 1 ? 's' : ''} ·{' '}
                              <span className="font-bold text-amber-700">{fmt(unpaidTotal)}</span> owed
                            </div>
                            <Button
                              size="sm"
                              disabled={!hasUnpaid || markingPaid === rep.name}
                              onClick={() => markAllPaid(rep.name, unpaidOrders.length, unpaidTotal)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7 gap-1"
                              data-testid={`mark-all-paid-${rep.name.replace(/ /g, '-')}`}
                              title={hasUnpaid ? `Mark all ${unpaidOrders.length} unpaid orders for ${rep.name} as PAID` : 'No unpaid orders'}
                            >
                              {markingPaid === rep.name ? '…marking' : <>💵 Mark All Paid Today</>}
                            </Button>
                          </div>

                          {Object.keys(ordersByMonth).length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-gray-600 mb-2">Payout Receipts</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(ordersByMonth).sort().reverse().map(key => {
                                  const [y, m] = key.split('-').map(Number);
                                  const hasPaid = ordersByMonth[key].some(o => o.commission_paid);
                                  const dlKey = `${rep.name}-${y}-${m}`;
                                  return (
                                    <div key={key} className="inline-flex rounded overflow-hidden border border-gray-300">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={!hasPaid || downloadingReceipt === dlKey}
                                        onClick={() => downloadReceipt(rep.name, y, m)}
                                        className="text-[10px] gap-1 h-7 rounded-none px-2 border-r border-gray-300 hover:bg-slate-50"
                                        data-testid={`receipt-${rep.name.replace(/ /g, '-')}-${key}`}
                                        title={hasPaid ? `Download ${MONTH_NAMES[m]} ${y} payout PDF` : 'No paid orders yet'}
                                      >
                                        <Download size={10} />
                                        {MONTH_NAMES[m]} {y}
                                        {!hasPaid && <span className="text-gray-400 ml-1">(no paid)</span>}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={!hasPaid || sendingStatement === dlKey}
                                        onClick={() => sendStatement(rep.name, y, m, rep.email)}
                                        className="text-[10px] gap-1 h-7 rounded-none px-2 hover:bg-indigo-50 text-indigo-700"
                                        data-testid={`send-${rep.name.replace(/ /g, '-')}-${key}`}
                                        title={hasPaid ? `Email ${MONTH_NAMES[m]} ${y} statement to ${rep.name}${rep.email ? ' <' + rep.email + '>' : ''}` : 'No paid orders yet'}
                                      >
                                        {sendingStatement === dlKey ? '…sending' : <>✉ Send</>}
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Year-End (1099 Recap) — one per year the rep had activity in */}
                          {(() => {
                            const years = Array.from(new Set(
                              (rep.orders || []).map(o => {
                                const d = o.commission_paid_date || o.closed_date;
                                return d ? new Date(d).getFullYear() : null;
                              }).filter(Boolean)
                            )).sort().reverse();
                            if (years.length === 0) return null;
                            return (
                              <div className="mt-2 pt-2 border-t border-dashed">
                                <p className="text-[10px] font-bold text-purple-700 mb-2">Year-End 1099 Recap</p>
                                <div className="flex flex-wrap gap-2">
                                  {years.map(y => {
                                    const key = `${rep.name}-${y}`;
                                    return (
                                      <div key={y} className="inline-flex rounded overflow-hidden border border-purple-300">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          disabled={downloadingAnnual === key}
                                          onClick={() => downloadAnnual(rep.name, y)}
                                          className="text-[10px] gap-1 h-7 rounded-none px-2 border-r border-purple-300 hover:bg-purple-50 text-purple-800"
                                          data-testid={`annual-${rep.name.replace(/ /g, '-')}-${y}`}
                                          title={`Download ${y} year-end 1099 recap PDF`}
                                        >
                                          <Download size={10} /> {y} Recap
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          disabled={sendingAnnual === key || !rep.email}
                                          onClick={() => sendAnnual(rep.name, y, rep.email)}
                                          className="text-[10px] gap-1 h-7 rounded-none px-2 hover:bg-purple-100 text-purple-800"
                                          data-testid={`send-annual-${rep.name.replace(/ /g, '-')}-${y}`}
                                          title={rep.email ? `Email ${y} year-end recap to ${rep.email}` : `No email on file for ${rep.name}`}
                                        >
                                          {sendingAnnual === key ? '…' : <><Mail size={10} /> Send Jan 1</>}
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ ALL ORDERS ═══ */}
      {view === 'orders' && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-400" />
              <select
                value={filterRep}
                onChange={e => setFilterRep(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white"
                data-testid="filter-rep"
              >
                <option value="">All Reps</option>
                {repData.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
              </select>
              <select
                value={filterPaid}
                onChange={e => setFilterPaid(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white"
                data-testid="filter-paid"
              >
                <option value="">All Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <span className="text-[10px] text-gray-400">{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="orders-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">NYC PO #</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Ref #</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">School</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">DBN</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Sales Rep</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Order Amt</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Comm %</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Commission</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">NYC Paid</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Comm Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Close Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">No commission orders found. Close a quote and assign a sales rep in the Fulfillment Tracker.</td></tr>
                ) : filteredOrders.map((o, i) => (
                  <tr key={i} className="border-b hover:bg-blue-50/30" data-testid={`order-row-${i}`}>
                    <td
                      className={`px-3 py-2 font-mono font-bold text-[#002FA7] text-[10px] ${canEditPo(o) ? 'cursor-pointer hover:bg-blue-100' : ''} ${poFlash === o.order_number ? 'bg-green-100' : ''}`}
                      onClick={() => editingPoKey === poKey(o) ? null : startEditPo(o)}
                      title={canEditPo(o) ? 'Click to edit NYC PO #' : 'Legacy quote — edit under Fulfillment Tracker'}
                      data-testid={`all-po-cell-${i}`}
                    >
                      {editingPoKey === poKey(o) ? (
                        <input
                          autoFocus
                          type="text"
                          className="border border-blue-500 rounded px-1 py-0.5 text-[10px] w-28 font-mono"
                          value={editingPoValue}
                          onChange={(e) => setEditingPoValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); savePo(o); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelEditPo(); }
                          }}
                          onBlur={() => setTimeout(() => { if (editingPoKey === poKey(o) && !savingPo) savePo(o); }, 150)}
                          disabled={savingPo}
                          placeholder="WO260…"
                        />
                      ) : (
                        o.po_number || <span className="text-gray-300 italic">click to add</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500 text-[10px]">{o.quote_number || o.order_number || <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[140px] truncate">{o.school_name}</td>
                    <td className="px-3 py-2 font-mono text-gray-500">{o.school_dbn}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{o.sales_rep}</td>
                    <td className="px-3 py-2 text-right">{fmt(o.subtotal)}</td>
                    <td className="px-3 py-2 text-center">{o.commission_pct}%</td>
                    <td className="px-3 py-2 text-right font-bold">{fmt(o.commission_amount)}</td>
                    <td className="px-3 py-2 text-center">
                      {o.payment_received ? <Check size={14} className="text-emerald-500 mx-auto" /> : <Clock size={14} className="text-gray-300 mx-auto" />}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {o.commission_paid
                        ? <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">PAID {o.commission_paid_date ? fmtDate(o.commission_paid_date) : ''}</span>
                        : o.payment_received
                          ? <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">OVERDUE</span>
                          : <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">PENDING</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmtDate(o.closed_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══ MONTHLY ═══ */}
      {view === 'monthly' && (
        <>
          <h3 className="text-sm font-bold text-gray-800">{currentYear} Monthly Commission Summary</h3>
          {monthlyData.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">
              No commission data for {currentYear} yet. Close orders and assign sales reps to see monthly breakdowns.
            </div>
          ) : (
            <>
              {/* Visual bar chart */}
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="monthly-chart">
                <div className="flex items-end gap-2 h-40">
                  {monthlyData.map(m => {
                    const maxEarned = Math.max(...monthlyData.map(x => x.commission_earned), 1);
                    const height = (m.commission_earned / maxEarned) * 100;
                    const [y, mo] = m.month.split('-').map(Number);
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                        <span className="text-[9px] font-bold text-emerald-700 mb-1">{fmt(m.commission_earned)}</span>
                        <div className="w-full flex flex-col items-center">
                          <div
                            className="w-8 bg-gradient-to-t from-[#002FA7] to-blue-400 rounded-t"
                            style={{ height: `${Math.max(height, 4)}%` }}
                          />
                          {m.commission_paid > 0 && (
                            <div
                              className="w-8 bg-emerald-400 rounded-b"
                              style={{ height: `${(m.commission_paid / maxEarned) * 100}%` }}
                            />
                          )}
                        </div>
                        <span className="text-[9px] text-gray-500 mt-1">{MONTH_NAMES[mo]?.slice(0, 3)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-500 justify-center">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gradient-to-t from-[#002FA7] to-blue-400 inline-block" /> Earned</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Paid</span>
                </div>
              </div>

              {/* Monthly table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="monthly-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Month</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-600">Orders</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Revenue</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Commission Earned</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Paid Out</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map(m => {
                      const [y, mo] = m.month.split('-').map(Number);
                      return (
                        <tr key={m.month} className="border-b hover:bg-blue-50/30">
                          <td className="px-3 py-2 font-medium">{MONTH_NAMES[mo]} {y}</td>
                          <td className="px-3 py-2 text-center">{m.orders}</td>
                          <td className="px-3 py-2 text-right">{fmt(m.revenue)}</td>
                          <td className="px-3 py-2 text-right font-bold text-[#002FA7]">{fmt(m.commission_earned)}</td>
                          <td className="px-3 py-2 text-right font-bold text-emerald-600">{fmt(m.commission_paid)}</td>
                          <td className="px-3 py-2 text-right font-bold text-amber-600">{fmt(m.commission_unpaid)}</td>
                        </tr>
                      );
                    })}
                    {/* Totals */}
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-3 py-2 text-center">{monthlyData.reduce((s, m) => s + m.orders, 0)}</td>
                      <td className="px-3 py-2 text-right">{fmt(monthlyData.reduce((s, m) => s + m.revenue, 0))}</td>
                      <td className="px-3 py-2 text-right text-[#002FA7]">{fmt(monthlyData.reduce((s, m) => s + m.commission_earned, 0))}</td>
                      <td className="px-3 py-2 text-right text-emerald-600">{fmt(monthlyData.reduce((s, m) => s + m.commission_paid, 0))}</td>
                      <td className="px-3 py-2 text-right text-amber-600">{fmt(monthlyData.reduce((s, m) => s + m.commission_unpaid, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══ MANAGE REPS ═══ */}
      {view === 'manage' && (
        <div className="space-y-4" data-testid="manage-reps-section">
          <h3 className="text-sm font-bold text-gray-800">Sales Rep Roster</h3>

          {/* Add New Rep */}
          <div className="bg-white border-2 border-dashed border-blue-200 rounded-lg p-4" data-testid="add-rep-form">
            <p className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1"><UserPlus size={14} /> Add New Sales Rep</p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-[10px] text-gray-500">Full Name</label>
                <Input
                  value={newRepName}
                  onChange={e => setNewRepName(e.target.value)}
                  placeholder="e.g. Jane Doe"
                  className="h-8 text-xs"
                  data-testid="new-rep-name"
                />
              </div>
              <div className="flex-[2]">
                <label className="text-[10px] text-gray-500">Territory</label>
                <Input
                  value={newRepTerritory}
                  onChange={e => setNewRepTerritory(e.target.value)}
                  placeholder="e.g. All Westchester Schools"
                  className="h-8 text-xs"
                  data-testid="new-rep-territory"
                />
              </div>
              <Button
                size="sm"
                onClick={addRep}
                disabled={repSaving || !newRepName.trim() || !newRepTerritory.trim()}
                className="bg-[#002FA7] h-8"
                data-testid="add-rep-btn"
              >
                <UserPlus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Rep List */}
          <div className="space-y-2">
            {repsList.map((rep, i) => (
              <div key={rep.rep_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`manage-rep-${rep.rep_id}`}>
                {editingRep === rep.rep_id ? (
                  <div className="p-3 bg-blue-50 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500">Name</label>
                        <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-xs" data-testid="edit-rep-name" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-gray-500">Territory (display label)</label>
                        <Input value={editTerritory} onChange={e => setEditTerritory(e.target.value)} className="h-7 text-xs" data-testid="edit-rep-territory" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500">Email (for auto-sent statements)</label>
                      <Input
                        type="email"
                        value={editEmail}
                        onChange={e => setEditEmail(e.target.value)}
                        placeholder="rep@example.com"
                        className="h-7 text-xs"
                        data-testid="edit-rep-email"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Auto-assign these boroughs to this rep</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { letter: 'M', name: 'Manhattan' },
                          { letter: 'K', name: 'Brooklyn' },
                          { letter: 'X', name: 'Bronx' },
                          { letter: 'Q', name: 'Queens' },
                          { letter: 'R', name: 'Staten Island' },
                        ].map(b => (
                          <label key={b.letter} className="flex items-center gap-1 text-xs cursor-pointer bg-white border border-gray-200 rounded px-2 py-0.5 hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={editBoroughs.includes(b.letter)}
                              onChange={e => {
                                if (e.target.checked) setEditBoroughs([...editBoroughs, b.letter]);
                                else setEditBoroughs(editBoroughs.filter(x => x !== b.letter));
                              }}
                              className="rounded"
                              data-testid={`edit-borough-${b.letter}`}
                            />
                            <span>{b.name} ({b.letter})</span>
                          </label>
                        ))}
                      </div>
                      {editBoroughs.includes('Q') && (
                        <div className="mt-2 bg-white border border-amber-200 rounded p-2">
                          <label className="text-[10px] text-amber-800 font-bold block mb-1">
                            Queens districts only (leave all unchecked to get ALL of Queens)
                          </label>
                          <div className="flex flex-wrap gap-1">
                            {[24, 25, 26, 27, 28, 29, 30].map(d => (
                              <label key={d} className="flex items-center gap-1 text-[11px] cursor-pointer bg-white border border-gray-200 rounded px-1.5">
                                <input
                                  type="checkbox"
                                  checked={editQueensDistricts.includes(d)}
                                  onChange={e => {
                                    if (e.target.checked) setEditQueensDistricts([...editQueensDistricts, d]);
                                    else setEditQueensDistricts(editQueensDistricts.filter(x => x !== d));
                                  }}
                                  data-testid={`edit-queens-d${d}`}
                                />
                                <span>D{d}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 justify-end pt-1">
                      <Button size="sm" onClick={() => saveEditRep(rep.rep_id)} disabled={repSaving} className="bg-emerald-600 h-7 text-xs" data-testid="save-edit-rep-btn">
                        <Save size={12} className="mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingRep(null)} className="h-7 text-xs" data-testid="cancel-edit-rep-btn">
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getRepColor(i)}`} />
                      <div>
                        <p className="font-bold text-sm text-gray-800">{rep.name}</p>
                        <p className="text-[10px] text-gray-500">{rep.territory}</p>
                        {rep.email && <p className="text-[10px] text-blue-600">✉ {rep.email}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {confirmDelete === rep.rep_id ? (
                        <>
                          <span className="text-[10px] text-red-600 font-bold mr-1">Delete?</span>
                          <Button size="sm" variant="destructive" className="h-6 text-[10px] px-2" onClick={() => deleteRep(rep.rep_id)} disabled={repSaving} data-testid="confirm-delete-rep-btn">
                            Yes
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-rep-btn">
                            No
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2"
                            onClick={() => {
                              setEditingRep(rep.rep_id);
                              setEditName(rep.name);
                              setEditTerritory(rep.territory);
                              setEditEmail(rep.email || '');
                              setEditBoroughs(rep.boroughs || []);
                              setEditQueensDistricts(rep.queens_districts_only || []);
                            }}
                            data-testid="edit-rep-btn"
                          >
                            <Edit2 size={10} className="mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2 text-red-500 hover:bg-red-50"
                            onClick={() => setConfirmDelete(rep.rep_id)}
                            data-testid="delete-rep-btn"
                          >
                            <Trash2 size={10} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {repsList.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">No sales reps found. Add one above.</div>
            )}
          </div>
        </div>
      )}

      {/* Territory Reference */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800" data-testid="territory-reference">
        <p className="font-bold mb-1">Sales Territory Reference</p>
        <ul className="list-disc pl-4 space-y-0.5">
          {repsList.length > 0 ? repsList.map(r => (
            <li key={r.rep_id}><strong>{r.name}:</strong> {r.territory}</li>
          )) : repData.map(r => (
            <li key={r.name}><strong>{r.name}:</strong> {r.territory}</li>
          ))}
        </ul>
        <p className="text-[10px] text-blue-500 mt-2">Commissions are paid out at month-end when qualifying (order closed, NYC payment received, commission marked as paid in Fulfillment Tracker).</p>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, value, label, color, isText, onClick, testid, hint }) => {
  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      title={hint || (onClick ? `Click to see ${label.toLowerCase()}` : undefined)}
      className={`bg-white border-2 border-gray-100 rounded-lg p-4 text-left w-full ${onClick ? 'cursor-pointer transition-all hover:shadow-md hover:border-[#002FA7]/40 hover:-translate-y-[1px]' : ''}`}
    >
      <Icon size={18} className={color} />
      <div className={`${isText ? 'text-xl' : 'text-3xl'} font-black mt-1`} style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      {onClick && (
        <div className="text-[10px] text-blue-600 font-bold mt-1 opacity-0 group-hover:opacity-100">
          ▸ click to drill in
        </div>
      )}
    </Wrap>
  );
};

export default CommissionTrackerTab;
